import { useQuery } from '@apollo/client';
import useConfig from 'hooks/useConfig';
import { GET_LATEST_VERSION_AND_PRODUCT } from 'graphql/secure/queries';
import { useParams } from 'react-router-dom';
import { ELanguage } from 'DesignSystem/types/language.types';
import { TRouteParamsWithId } from 'types/router.types';
import { IProduct, IProductVersion } from '../types/product.types';
import { StringParam, useQueryParam } from 'use-query-params';

interface IUseProductProps {
  loading: boolean;
  error?: unknown;
  currentProductId?: string;
  product?: IProduct;
  latestProductVersion?: IProductVersion;
  lang?: ELanguage;
}

/**
 *  This dialog gets called from a dialog and the dialog context is not aware of any auth props
 */
export const useProduct = (productId?: string): IUseProductProps => {
  const params = useParams<TRouteParamsWithId>();

  const { appQueryParams } = useConfig();
  const [lang] = useQueryParam(appQueryParams.lang, StringParam);

  const { data, loading, error } = useQuery<{
    product: IProduct;
    latestProductVersion: IProductVersion;
  }>(GET_LATEST_VERSION_AND_PRODUCT, {
    skip: !params.id && !productId,
    variables: {
      id: params.id || productId,
      lang,
    },
  });
  // Use the default language as fallback since the lang query param will be empty if it's the default language
  const languageWithFallback = lang || data?.product?.languageConfig?.default;

  return {
    loading,
    error,
    currentProductId: params.id || productId,
    product: data?.product,
    latestProductVersion: data?.latestProductVersion,
    // Make sure the lang is actually a language that's defined, since it can be changed in the url
    lang: Object.values(ELanguage).includes(languageWithFallback as ELanguage)
      ? (languageWithFallback as ELanguage)
      : undefined,
  };
};
